import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MaterialModule } from '@app/material.module';
import { ModulesService } from '@shared/services/modules.service';
import { UsersService } from '@shared/services/entities/users.service';
import { RolesService } from '@shared/services/entities/roles.service';

interface MenuCard { title: string; description: string; icon: string; route: string; }

/** Tarjetas por omisión (si todavía no se siembran los módulos en BD). */
const DEFAULT_CARDS: MenuCard[] = [
  {
    title: 'Folio',
    description: 'Salida de material para cirugía: nuevo folio y folios guardados como borrador.',
    icon: 'post_add',
    route: '/interventions/folios',
  },
  {
    title: 'Remisión',
    description: 'Material entregado con cargo: nueva remisión y remisiones guardadas como borrador.',
    icon: 'receipt_long',
    route: '/interventions/remissions',
  },
];

/**
 * Salida de material: menú con los dos procesos. Cada uno tiene su propia
 * pantalla con su tabla (Folios → borradores y folios; Remisiones →
 * borradores de remisión).
 *
 * Si en la tabla `modules` existen los sub-módulos `/interventions/folios`
 * y `/interventions/remissions`, las tarjetas salen de ahí (etiqueta, ícono,
 * descripción, activo) y solo se muestran las que el rol tiene asignadas.
 * Si no existen, se muestran las dos tarjetas por omisión.
 */
@Component({
  standalone: true,
  imports: [PageLoaderComponent, PageTitleComponent, MaterialModule],
  selector: 'app-interventions-menu',
  templateUrl: './interventions-menu.component.html',
  styleUrls: ['../../audit/menu/audit-menu.component.scss'],
})
export class InterventionsMenuComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly modulesService = inject(ModulesService);
  private readonly usersService = inject(UsersService);
  private readonly rolesService = inject(RolesService);

  title = this.modulesService.moduleTitle('/interventions', 'Salida de material');
  menuCards = signal<MenuCard[]>(DEFAULT_CARDS);
  loaded = signal(false);

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    try {
      const resp: any = await this.modulesService.search({ searchType: 'default', condition: { deleted: false } });
      const norm = (r: any) => String(r ?? '').split('?')[0].replace(/\/+$/, '');
      const subs = ((resp?.data ?? []) as any[]).filter(m => DEFAULT_CARDS.some(c => c.route === norm(m.route)));
      if (subs.length === 0) return; // sin sembrar: tarjetas por omisión

      const user = this.usersService.currentUser() ?? await this.usersService.loadCurrentUser();
      let nav: string[] | null = null;
      if (user?.role) {
        const rolesResp: any = await this.rolesService.search({ searchType: 'default', condition: { id: user.role } });
        const role = rolesResp?.data?.[0];
        nav = Array.isArray(role?.navAccessTree) ? role.navAccessTree : null;
      }
      const cards = DEFAULT_CARDS.map(card => {
        const mod = subs.find(m => norm(m.route) === card.route);
        if (!mod) return card; // ese sub-módulo no se sembró: se deja la tarjeta
        if (mod.active === false) return null;
        if (nav && !nav.includes(mod.id)) return null;
        return {
          title: (mod.label || mod.name || card.title),
          description: mod.description || card.description,
          icon: mod.icon || card.icon,
          route: card.route,
        } as MenuCard;
      }).filter((c): c is MenuCard => !!c);
      this.menuCards.set(cards);
    } catch {
      /* si algo falla se quedan las tarjetas por omisión */
    } finally {
      this.loaded.set(true);
    }
  }

  navigationTo(route: string): void {
    this.router.navigate([route]);
  }
}
